import React, {
  MouseEvent,
} from 'react';
import {
  Row,
  Table,
  TableMeta,
} from '@tanstack/react-table';
import {
  useNavigate,
} from 'react-router-dom';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandRoundedIcon from '@mui/icons-material/ExpandRounded';
import CompressRoundedIcon from '@mui/icons-material/CompressRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
  notEmptyString,
} from '@populateui/utils';
import ToolTip from 'core/components/Tooltip';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  useMedia,
} from 'core/hooks';
import Name from 'pages/Dashboard/components/Name';
import {
  getPatientChartPath,
} from 'pages/Dashboard/pages/Charts/utils';
import {
  CcmMonthlyCallRow,
  CcmMonthlyRow,
} from 'pages/Dashboard/pages/Dashboard/CcmMonthly/types';
import {
  formatDuration,
} from 'pages/Dashboard/pages/Dashboard/CcmMonthly/helper';
import {
  formatDateTime,
} from 'utils/date';

type CcmMonthlyTableMeta = TableMeta<CcmMonthlyRow> & {
  onOpenCallDetail: (call: CcmMonthlyCallRow) => void;
};

type ToggleRowCellProps = {
  row: Row<CcmMonthlyRow>;
};

type PatientCellProps = {
  row: Row<CcmMonthlyRow>;
};

function ToggleRowCell({ row }: ToggleRowCellProps) {
  const expandable = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const Icon = isExpanded && expandable ? ExpandMoreIcon : ChevronRightIcon;
  const tooltip = isExpanded ? 'Collapse' : 'Expand';

  return !row.original.isCallDetail && (
    <ToolTip title={!expandable ? 'No calls' : tooltip}>
      <span>
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            row.toggleExpanded();
          }}
          disabled={!expandable}
          className={clsx('!p-1', { '!cursor-not-allowed !pointer-events-auto opacity-30': !expandable })}
          disableRipple={!expandable}
        >
          <Icon className="!text-gray-500" />
        </IconButton>
      </span>
    </ToolTip>
  );
}

function CallDetailPatientCell({ row }: PatientCellProps) {
  const calledOn = row.original.isCallDetail ? row.original.calledOn : null;

  return (
    <div className="flex min-w-0 items-center pl-2">
      <span className="text-xs font-medium leading-4 text-gray-700">
        {notEmptyString(calledOn) ? formatDateTime(calledOn ?? '') : DEFAULT_EMPTY_VALUE}
      </span>
    </div>
  );
}

function DefaultPatientCell({ row }: PatientCellProps) {
  const { getMediaPathById } = useMedia();
  const navigate = useNavigate();
  const src = getMediaPathById(row.original.patientId ?? 0) ?? undefined;
  const link = getPatientChartPath(row.original.patientId);

  return (
    <Name
      link={{
        to: link,
        onClick: (event: MouseEvent<HTMLAnchorElement>) => {
          event.stopPropagation();
          if (!isNil(link)) {
            navigate(link);
          }
        },
      }}
      name={row.original.patientName ?? DEFAULT_EMPTY_VALUE}
      image={{ src }}
      classes={{
        root: '!min-w-0 !font-normal',
        nameRoot: '!min-w-0',
        link: '!w-fit',
        label: '!text-xs !font-medium !leading-4',
      }}
    />
  );
}

function PatientCell({ row }: PatientCellProps) {
  const Content = row.original.isCallDetail ? CallDetailPatientCell : DefaultPatientCell;

  return <Content row={row} />;
}

type TextCellProps = {
  value?: string | null;
};

function PrimaryTextCell({ value }: TextCellProps) {
  return (
    <span className="text-xs font-medium leading-4 text-gray-700">
      {notEmptyString(value) ? value : DEFAULT_EMPTY_VALUE}
    </span>
  );
}

function DateTimeCell({ value }: TextCellProps) {
  return (
    <span className="text-xs font-medium leading-4 text-gray-700">
      {notEmptyString(value) ? formatDateTime(value ?? '') : DEFAULT_EMPTY_VALUE}
    </span>
  );
}

type CountCellProps = {
  value?: number | null;
};

function CountCell({ value }: CountCellProps) {
  return (
    <span className="text-xs font-medium leading-4 text-gray-700">
      {value ?? DEFAULT_EMPTY_VALUE}
    </span>
  );
}

type ActionCellProps = {
  row: Row<CcmMonthlyRow>;
  tableMeta?: CcmMonthlyTableMeta;
};

function ActionCell({
  row,
  tableMeta,
}: ActionCellProps) {
  const callDetail = row.original.isCallDetail ? row.original : null;

  return callDetail && tableMeta?.onOpenCallDetail ? (
    <ToolTip title="View call details">
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          tableMeta.onOpenCallDetail(callDetail);
        }}
        className="!p-1 text-gray-500 hover:!text-blue-600"
      >
        <OpenInNewRoundedIcon className="!h-4 !w-4" />
      </IconButton>
    </ToolTip>
  ) : null;
}

function getToggleColumnTitle<T>(table: Table<T>) {
  const isAllRowsExpanded = table.getIsAllRowsExpanded();
  const Icon = isAllRowsExpanded ? CompressRoundedIcon : ExpandRoundedIcon;

  return (
    <ToolTip
      title={isAllRowsExpanded ? 'Collapse all' : 'Expand all'}
      disableFocusListener={false}
    >
      <IconButton
        onClick={table.getToggleAllRowsExpandedHandler()}
        disableRipple
        className="!p-1"
      >
        <Icon className="!relative !left-1 !top-0.5 !h-4 !w-4 !text-gray-500" />
      </IconButton>
    </ToolTip>
  );
}

export const columns: ColumnDef<CcmMonthlyRow>[] = [{
  id: 'toggleRow',
  size: 48,
  columnTitle: getToggleColumnTitle,
  cell: ({ row }) => <ToggleRowCell row={row} />,
}, {
  id: 'patientName',
  accessorKey: 'patientName',
  header: 'Patient',
  minSize: 240,
  meta: { autoSized: true },
  cell: ({ row }) => <PatientCell row={row} />,
}, {
  id: 'calls',
  accessorKey: 'callCount',
  header: 'Calls',
  size: 100,
  cell: ({ row: { original } }) => (
    original.isCallDetail
      ? null
      : <CountCell value={original.callCount} />
  ),
}, {
  id: 'latestCallOn',
  accessorKey: 'latestCallOn',
  header: 'Latest call',
  size: 140,
  cell: ({ row: { original } }) => (
    original.isCallDetail
      ? <PrimaryTextCell value={`Attempt #${original.attemptNumber ?? 1}`} />
      : <DateTimeCell value={original.latestCallOn} />
  ),
}, {
  id: 'durationSeconds',
  accessorKey: 'durationSeconds',
  header: 'Duration',
  size: 120,
  cell: ({ row : { original } }) => (
    original.isCallDetail
      ? <PrimaryTextCell value={formatDuration(original.durationSeconds)} />
      : <PrimaryTextCell value={formatDuration(original.totalDurationSeconds)} />
  ),
}, {
  id: 'actions',
  header: '',
  size: 80,
  cell: ({ row, table }) => <ActionCell row={row} tableMeta={table.options.meta as CcmMonthlyTableMeta | undefined} />,
}];

export default columns;
